<template>
    <div class="facility-management-dashboard">
        <!-- Sidebar KPIs -->
        <aside class="kpi-sidebar">
            <h3>{{ $t('dashboards.facility-managers.kpiTitle') }}</h3>
            <ul class="kpi-list">
                <li v-for="kpi in kpis" :key="kpi.id" :class="['kpi-item', { active: kpi.id === activeKpiId }]"
                    @click="setActiveKpi(kpi.id)" @keyup.enter="setActiveKpi(kpi.id)" role="button" tabindex="0"
                    :aria-selected="kpi.id === activeKpiId">
                    <img class="kpi-icon" :src="kpi.id === activeKpiId ? kpi.icon.active : kpi.icon.inactive"
                        :alt="kpi.name + ' icon'" />
                    <span>{{ $t(kpi.name) }}</span>
                </li>
            </ul>
        </aside>

        <!-- Main Dashboard -->
        <main class="dashboard-main-content">
            <!-- KPI 1: Real-time Equipment & Asset Status -->
            <div v-if="activeKpiId === 1" class="dashboard-view">
                <!-- Critical Alerts & Equipment Status -->
                <div class="kpi-cards-grid equipment-status">
                    <div class="status-card critical">
                        <div class="status-header">
                            <h4>{{ $t('dashboards.facility-managers.criticalAlerts') }}</h4>
                            <span class="alert-count">{{ currentData?.equipment?.criticalAssets?.offline || 3 }}</span>
                        </div>
                        <div class="alert-list">
                            <div class="alert-item">{{ $t('dashboards.facility-managers.alert1') }}</div>
                            <div class="alert-item">{{ $t('dashboards.facility-managers.alert2') }}</div>
                            <div class="alert-item">{{ $t('dashboards.facility-managers.alert3') }}</div>
                        </div>
                    </div>

                    <div class="status-card warning">
                        <div class="status-header">
                            <h4> {{ $t('dashboards.facility-managers.preventiveMaintenanceDue') }}</h4>
                            <span class="alert-count">{{ currentData?.equipment?.maintenanceScheduled || 8 }}</span>
                        </div>
                        <div class="maintenance-list">
                            <div class="maintenance-item">{{ $t('dashboards.facility-managers.next7Days') }}</div>
                            <div class="maintenance-item">{{ $t('dashboards.facility-managers.next30Days') }}</div>
                        </div>
                    </div>

                    <div class="status-card success">
                        <div class="status-header">
                            <h4> {{ $t('dashboards.facility-managers.equipmentUptime') }}</h4>
                            <span class="uptime-percent">{{ currentData?.equipment?.uptimePercentage || 94.2 }}%</span>
                        </div>
                        <div class="uptime-details">
                            <div class="detail-item">{{ $t('dashboards.facility-managers.uptimeTarget') }}</div>
                            <div class="detail-item trend-down">{{ $t('dashboards.facility-managers.uptimeChange') }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Equipment Performance Charts -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.facility-managers.assetHealthScore') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.facility-managers.assetHealthNote') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="bar" height="280" :options="assetHealthChart.options"
                                :series="assetHealthChart.series" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.facility-managers.maintenanceType') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.facility-managers.maintenanceTypeNote') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="donut" height="280" :options="maintenanceTypeChart.options"
                                :series="maintenanceTypeChart.series" />
                        </div>
                    </div>
                </div>

                <!-- Equipment Timeline -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4>{{ $t('dashboards.facility-managers.uptimeTrend') }}</h4>

                        <div class="chart-controls">
                            <div class="uptime-legend">
                                <span class="legend-item">
                                    <span class="legend-dot uptime"></span>
                                    <span> {{ $t('dashboards.facility-managers.uptimeTarget') }}</span>
                                </span>
                                <span class="legend-item">
                                    <span class="legend-dot actual"></span>
                                    <span> {{ $t('dashboards.facility-managers.uptimeActual') }}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart type="line" height="250" :options="uptimeChart.options"
                            :series="uptimeChart.series" />
                    </div>
                </div>
            </div>

            <!-- KPI 2: Energy Management & Consumption -->
            <div v-else-if="activeKpiId === 2" class="dashboard-view">
                <!-- Energy KPIs -->
                <div class="kpi-cards-grid energy-cards">
                    <div class="energy-card">
                        <div class="energy-value">2.8</div>
                        <div class="energy-label"> {{ $t('dashboards.facility-managers.energyIntensity') }}</div>
                        <div class="energy-comparison benchmark-good">
                            {{ $t('dashboards.facility-managers.energyBenchmarkComparison') }}
                        </div>
                    </div>

                    <div class="energy-card">
                        <div class="energy-value">€18,420</div>
                        <div class="energy-label">{{ $t('dashboards.facility-managers.monthlyEnergyCost') }}</div>
                        <div class="energy-comparison trend-up">{{
                            $t('dashboards.facility-managers.monthlyEnergyChange') }}</div>
                    </div>

                    <div class="energy-card">
                        <div class="energy-value">847</div>
                        <div class="energy-label">{{ $t('dashboards.facility-managers.kwhToday') }}</div>
                        <div class="energy-comparison trend-down"> {{
                            $t('dashboards.facility-managers.dailyEnergyChange') }}</div>
                    </div>
                </div>

                <!-- Energy Analytics -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.facility-managers.energyBySystem') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.facility-managers.energyBySystemNote') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="treemap" height="280" :options="energySystemChart.options"
                                :series="energySystemChart.series" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.facility-managers.energyCostBenchmark') }}</h4>
                            <div class="benchmark-info">
                                <span class="benchmark-text"> {{ $t('dashboards.facility-managers.industryAverage')
                                }}</span>
                            </div>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="line" height="280" :options="energyBenchmarkChart.options"
                                :series="energyBenchmarkChart.series" />
                        </div>
                    </div>
                </div>

                <!-- 24-Hour Energy Profile -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4> {{ $t('dashboards.facility-managers.energyProfile') }}</h4>
                        <div class="chart-controls">
                            <div class="time-selector">
                                <button class="time-btn" :class="{ active: selectedPeriod === 'today' }"
                                    @click="switchPeriod('today')">
                                    {{ $t('dashboards.facility-managers.today') }}
                                </button>
                                <button class="time-btn" :class="{ active: selectedPeriod === 'yesterday' }"
                                    @click="switchPeriod('yesterday')">
                                    {{ $t('dashboards.facility-managers.yesterday') }}
                                </button>
                                <button class="time-btn" :class="{ active: selectedPeriod === 'lastWeek' }"
                                    @click="switchPeriod('lastWeek')">
                                    {{ $t('dashboards.facility-managers.lastWeek') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart type="area" height="250" :options="hourlyEnergyChart.options"
                            :series="hourlyEnergyChart.series" />
                    </div>
                </div>
            </div>

            <!-- KPI 3: Space Utilization & Occupancy -->
            <div v-else-if="activeKpiId === 3" class="dashboard-view">
                <!-- Space Metrics -->
                <div class="space-utilization space-cards">
                    <div class="space-card">
                        <div class="space-value">68%</div>
                        <div class="space-label"> {{ $t('dashboards.facility-managers.overallUtilization') }}</div>
                        <div class="space-target"> {{ $t('dashboards.facility-managers.uptimeTarget75') }}</div>
                    </div>

                    <div class="space-card">
                        <div class="space-value">142</div>
                        <div class="space-label"> {{ $t('dashboards.facility-managers.peopleOnsite') }}</div>
                        <div class="space-capacity">{{ $t('dashboards.facility-managers.capacity') }}</div>
                    </div>

                    <div class="space-card">
                        <div class="space-value">23</div>
                        <div class="space-label">{{ $t('dashboards.facility-managers.underutilizedSpaces') }}</div>
                        <div class="space-action"> {{ $t('dashboards.facility-managers.attentionRequired') }}</div>
                    </div>

                    <div class="space-card">
                        <div class="space-value">€2.15</div>
                        <div class="space-label">{{ $t('dashboards.facility-managers.costPerSqft') }}</div>
                        <div class="space-benchmark">{{ $t('dashboards.facility-managers.marketAverage') }}</div>
                    </div>
                </div>

                <!-- Space Analysis Charts -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.facility-managers.utilizationByFloor') }}</h4>
                            <p class="chart-subtitle">{{ $t('dashboards.facility-managers.utilizationByFloorNote') }}
                            </p>
                        </div>
                        <div class="space-utilization-chart">
                            <apexchart type="bar" height="280" :options="spaceUtilizationChart.options"
                                :series="spaceUtilizationChart.series" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.facility-managers.peakUsagePatterns') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.facility-managers.peakUsageNote') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="heatmap" height="280" :options="occupancyHeatmapChart.options"
                                :series="occupancyHeatmapChart.series" />
                        </div>
                    </div>
                </div>

                <!-- Weekly Occupancy Trend -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4> {{ $t('dashboards.facility-managers.weeklyOccupancy') }}</h4>
                        <div class="chart-controls">
                            <div class="occupancy-legend">
                                <span class="legend-item">
                                    <span class="legend-bar occupancy"></span>
                                    <span> {{ $t('dashboards.facility-managers.actualOccupancy') }}</span>
                                </span>
                                <span class="legend-item">
                                    <span class="legend-bar capacity"></span>
                                    <span> {{ $t('dashboards.facility-managers.availableCapacity') }}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart type="bar" height="250" :options="weeklyOccupancyChart.options"
                            :series="weeklyOccupancyChart.series" />
                    </div>
                </div>
            </div>

            <!-- KPI 4: Environmental Monitoring -->
            <div v-else-if="activeKpiId === 4" class="dashboard-view">
                <!-- Environmental Status -->
                <div class="env-status-grid">
                    <div class="env-card temperature">
                        <!-- <div class="env-icon">🌡️</div> -->
                        <span class="icon-wrap">
                            <img :src="icons.temperatureLevel" alt="" />
                        </span>
                        <div class="env-value">22.5°C</div>
                        <div class="env-label">{{ $t('dashboards.facility-managers.avgTemperature') }}</div>
                        <div class="env-status good">{{ $t('dashboards.facility-managers.temperatureStatus') }}</div>
                    </div>

                    <div class="env-card humidity">
                        <!-- <div class="env-icon">💧</div> -->
                        <span class="icon-wrap">
                            <img :src="icons.humidityLevel" alt="" />
                        </span>
                        <div class="env-value">45%</div>
                        <div class="env-label">{{ $t('dashboards.facility-managers.humidityLevel') }}</div>
                        <div class="env-status good">{{ $t('dashboards.facility-managers.humidityStatus') }}</div>
                    </div>

                    <div class="env-card air-quality">
                        <!-- <div class="env-icon">🌬️</div> -->
                        <span class="icon-wrap">
                            <img :src="icons.airQualityLevel" alt="" />
                        </span>
                        <div class="env-value">{{ $t('dashboards.facility-managers.good') }}</div>
                        <div class="env-label"> {{ $t('dashboards.facility-managers.airQualityIndex')
                        }}</div>
                        <div class="env-status good">PM2.5: 8 μg/m³ </div>
                    </div>

                    <div class="env-card co2">
                        <!-- <div class="env-icon">💨</div> -->
                        <span class="icon-wrap">
                            <img :src="icons.co2Level" alt="" />
                        </span>
                        <div class="env-value">420</div>
                        <div class="env-label"> {{ $t('dashboards.facility-managers.co2Level') }}</div>
                        <div class="env-status warning">{{ $t('dashboards.facility-managers.co2Status') }}</div>
                    </div>
                </div>

                <!-- Environmental Monitoring Charts -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.facility-managers.hvacPerformance') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.facility-managers.hvacPerformanceNote') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="scatter" height="280" :options="hvacPerformanceChart.options"
                                :series="hvacPerformanceChart.series" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.facility-managers.complianceScore') }}</h4>
                            <div class="compliance-targets">
                                <span class="target-item">{{ $t('dashboards.facility-managers.complianceTemp') }}</span>
                                <span class="target-item">{{ $t('dashboards.facility-managers.complianceHumidity')
                                }}</span>
                                <span class="target-item">{{ $t('dashboards.facility-managers.complianceCarbon')
                                }}</span>
                            </div>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="radialBar" height="280" :options="complianceChart.options"
                                :series="complianceChart.series" />
                        </div>
                    </div>
                </div>

                <!-- 24-Hour Environmental Trend -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4>{{
                            $t('dashboards.facility-managers.environmentalMonitoring24h') }}</h4>
                        <div class="chart-controls">
                            <select v-model="envMetric" class="filter-select">
                                <option value="temperature"> {{
                                    $t('dashboards.facility-managers.temperature') }}</option>
                                <option value="humidity"> {{ $t('dashboards.facility-managers.humidity') }}
                                </option>
                                <option value="co2"> {{ $t('dashboards.facility-managers.co2') }}</option>
                                <option value="airQuality">{{ $t('dashboards.facility-managers.airQuality')
                                    }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart type="line" height="250" :options="environmentalTrendChart.options"
                            :series="environmentalTrendChart.series" />
                    </div>
                </div>
            </div>

            <!-- KPI 5: Vendor Performance & SLA Tracking -->
            <div v-else-if="activeKpiId === 5" class="dashboard-view">
                <!-- Vendor Performance Metrics -->
                <div class="vendor-metrics-grid">
                    <div class="vendor-card">
                        <div class="vendor-value">92%</div>
                        <div class="vendor-label"> {{ $t('dashboards.facility-managers.slaCompliance') }}</div>
                        <div class="vendor-trend good">{{ $t('dashboards.facility-managers.slaChange') }}</div>
                    </div>

                    <div class="vendor-card">
                        <div class="vendor-value">2.3h</div>
                        <div class="vendor-label">{{ $t('dashboards.facility-managers.avgResponseTime') }}</div>
                        <div class="vendor-sla"> {{ $t('dashboards.facility-managers.slaTarget') }}</div>
                    </div>

                    <div class="vendor-card">
                        <div class="vendor-value">€24,580</div>
                        <div class="vendor-label">{{ $t('dashboards.facility-managers.monthlyVendorCosts') }}</div>
                        <div class="vendor-budget">{{ $t('dashboards.facility-managers.vendorBudget') }}</div>
                    </div>

                    <div class="vendor-card">
                        <div class="vendor-value">5</div>
                        <div class="vendor-label"> {{ $t('dashboards.facility-managers.activeVendors') }}</div>
                        <div class="vendor-contracts"> {{ $t('dashboards.facility-managers.vendorContracts') }}</div>
                    </div>
                </div>

                <!-- Vendor Analysis -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.facility-managers.vendorScorecard') }}</h4>
                            <p class="chart-subtitle">
                                {{ $t('dashboards.facility-managers.vendorScorecardNote') }}
                            </p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="radar" height="300" :options="vendorScorecardChart.options"
                                :series="vendorScorecardChart.series" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.facility-managers.vendorCostAnalysis') }}</h4>
                            <p class="chart-subtitle">{{ $t('dashboards.facility-managers.vendorCostNote') }}</p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="bar" height="300" :options="vendorCostChart.options"
                                :series="vendorCostChart.series" />
                        </div>
                    </div>
                </div>

                <!-- Response Time Trends -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4>{{ $t('dashboards.facility-managers.vendorResponseTrends') }}</h4>
                        <div class="sla-indicators">
                            <span class="sla-item good">✓ {{ $t('dashboards.facility-managers.responseHvac') }}</span>
                            <span class="sla-item warning">⚠ {{ $t('dashboards.facility-managers.responseElectrical')
                            }}</span>
                            <span class="sla-item critical">✗ {{ $t('dashboards.facility-managers.responsePlumbing')
                            }}</span>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart type="line" height="250" :options="responseTimeChart.options"
                            :series="responseTimeChart.series" />
                    </div>
                </div>
            </div>

            <!-- KPI 6: Predictive Analytics & Maintenance Planning -->
            <div v-else-if="activeKpiId === 6" class="dashboard-view">
                <!-- Predictive Insights -->
                <div class="prediction-cards-grid">
                    <div class="prediction-card high-risk">
                        <div class="prediction-header">
                            <h4> {{ $t('dashboards.facility-managers.highRiskEquipment') }}</h4>
                            <span class="risk-count">3</span>
                        </div>
                        <div class="risk-list">
                            <div class="risk-item"> {{ $t('dashboards.facility-managers.risk1') }}</div>
                            <div class="risk-item"> {{ $t('dashboards.facility-managers.risk2') }}</div>
                            <div class="risk-item">{{ $t('dashboards.facility-managers.risk3') }}</div>
                        </div>
                        <button class="action-btn"> {{
                            $t('dashboards.facility-managers.scheduleInspections') }}</button>
                    </div>

                    <div class="prediction-card budget-forecast">
                        <div class="prediction-header">
                            <h4>{{ $t('dashboards.facility-managers.nextQuarterForecast') }}</h4>
                        </div>
                        <div class="budget-items">
                            <div class="budget-item">{{
                                $t('dashboards.facility-managers.forecastMaintenance') }}</div>
                            <div class="budget-item">{{
                                $t('dashboards.facility-managers.forecastReplacements') }}</div>
                            <div class="budget-item"> {{
                                $t('dashboards.facility-managers.forecastEnergy') }}</div>
                        </div>
                        <div class="total-forecast"> {{
                            $t('dashboards.facility-managers.forecastTotal') }}</div>
                    </div>
                </div>

                <!-- Predictive Analytics Charts -->
                <div class="charts-grid">
                    <div class="chart-container">
                        <div class="chart-header">
                            <h4>{{ $t('dashboards.facility-managers.failurePrediction') }} </h4>
                            <p class="chart-subtitle">{{ $t('dashboards.facility-managers.failurePredictionNote') }}</p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="scatter" height="300" :options="failurePredictionChart.options"
                                :series="failurePredictionChart.series" />
                        </div>
                    </div>

                    <div class="chart-container">
                        <div class="chart-header">
                            <h4> {{ $t('dashboards.facility-managers.costForecast') }}</h4>
                            <p class="chart-subtitle"> {{ $t('dashboards.facility-managers.costForecastNote') }}</p>
                        </div>
                        <div class="chart-wrapper">
                            <apexchart type="line" height="300" :options="costForecastChart.options"
                                :series="costForecastChart.series" />
                        </div>
                    </div>
                </div>

                <!-- Asset Lifecycle Management -->
                <div class="chart-container">
                    <div class="chart-header">
                        <h4>{{ $t('dashboards.facility-managers.assetLifecycle') }}</h4>
                        <div class="lifecycle-legend">
                            <span class="lifecycle-item new">■ {{ $t('dashboards.facility-managers.lifecycleNew')
                                }}</span>
                            <span class="lifecycle-item good">■ {{ $t('dashboards.facility-managers.lifecycleGood')
                                }}</span>
                            <span class="lifecycle-item aging">■ {{ $t('dashboards.facility-managers.lifecycleAging')
                                }}</span>
                            <span class="lifecycle-item replace">■ {{
                                $t('dashboards.facility-managers.lifecycleReplace') }}</span>
                        </div>
                    </div>
                    <div class="chart-wrapper">
                        <apexchart type="bar" height="250" :options="assetLifecycleChart.options"
                            :series="assetLifecycleChart.series" />
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { getFacilityManagementData } from "./facilityManagementData";

/*KPI icon set (active/inactive) */
import settingsActive from "@/assets/client-segments/kpis-icons/settings-active.svg";
import settingsInactive from "@/assets/client-segments/kpis-icons/settings-inactive.svg";
import buildingActive from "@/assets/client-segments/kpis-icons/building-active.svg";
import buildingInactive from "@/assets/client-segments/kpis-icons/building-inactive.svg";
import handshakeActive from "@/assets/client-segments/kpis-icons/handshake-active.svg";
import handshakeInactive from "@/assets/client-segments/kpis-icons/handshake-inactive.svg";
import analysisActive from "@/assets/client-segments/kpis-icons/analysis-active.svg";
import analysisInactive from "@/assets/client-segments/kpis-icons/analysis-inactive.svg";
import energyConsumptionActive from "@/assets/client-segments/kpis-icons/energy-consumption-active.svg";
import energyConsumptionInactive from "@/assets/client-segments/kpis-icons/energy-consumption-inactive.svg";
import leafActive from "@/assets/client-segments/kpis-icons/leaf-active.svg";
import leafInactive from "@/assets/client-segments/kpis-icons/leaf-inactive.svg";

/*Icons */
import temperatureLevel from "@/assets/client-segments/temperature-level.svg";
import humidityLevel from "@/assets/client-segments/humidity-level.svg";
import airQualityLevel from "@/assets/client-segments/air-quality-level.svg";
import co2Level from "@/assets/client-segments/co2-level.svg";

// Main state
const activeKpiId = ref(1);
const selectedWidget = ref(null);
const { t } = useI18n();

// Data from JS file
const facilityData = ref(null);
const currentData = ref(null);

onMounted(() => {
    facilityData.value = getFacilityManagementData();
    currentData.value = facilityData.value.getCurrentMonthData();
})

const icons = {
    temperatureLevel,
    humidityLevel,
    airQualityLevel,
    co2Level,
};

const kpis = ref([
    {
        id: 1,
        name: t("dashboards.facility-managers.equipmentAssetStatus"),
        icon: {
            active: settingsActive,
            inactive: settingsInactive,
        },
    },
    {
        id: 2,
        name: t("dashboards.facility-managers.energyManagement"),
        icon: {
            active: energyConsumptionActive,
            inactive: energyConsumptionInactive,
        },
    },
    {
        id: 3,
        name: t("dashboards.facility-managers.spaceUtilization"),
        icon: { active: buildingActive, inactive: buildingInactive },
    },
    {
        id: 4,
        name: t("dashboards.facility-managers.environmentalMonitoring"),
        icon: { active: leafActive, inactive: leafInactive },
    },
    {
        id: 5,
        name: t("dashboards.facility-managers.vendorPerformance"),
        icon: { active: handshakeActive, inactive: handshakeInactive },
    },
    {
        id: 6,
        name: t("dashboards.facility-managers.predictiveAnalytics"),
        icon: { active: analysisActive, inactive: analysisInactive },
    },
]);

function formatNumber(num) {
    return num.toLocaleString('de-DE');
}

function setActiveKpi(kpiId) {
    activeKpiId.value = kpiId;
    selectedWidget.value = null;
}

// Interactive functions
function showKpiDetails(widget) {
    selectedWidget.value = selectedWidget.value === widget ? null : widget;
    console.log(`Showing details for: ${widget}`);
}

// Asset Health Chart
const assetHealthChart = computed(() => ({
    series: [{
        name: t("dashboards.facility-managers.healthScore"),
        data: currentData.value?.equipment?.assetHealth?.map(a => a.score) || [95, 78, 65, 88, 92, 45, 83],
    }],
    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#22c55e"],
        xaxis: {
            categories: [
                t("dashboards.facility-managers.hvac"),
                t("dashboards.facility-managers.electrical"),
                t("dashboards.facility-managers.plumbing"),
                t("dashboards.facility-managers.elevators"),
                t("dashboards.facility-managers.security"),
                t("dashboards.facility-managers.boilers"),
                t("dashboards.facility-managers.lighting"),
            ],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
            min: 0,
            max: 100,
        },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 4 } },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val ? val + "%" : "0%";
            },
            style: { colors: ["#ffffff"] },
        },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + " " + t("dashboards.facility-managers.healthUnit") : t("dashboards.facility-managers.zeroHealth");
                },
            },
        },

    },
}));
// Equipment Uptime Chart
const uptimeChart = computed(() => ({
    series: [
        {
            name: t("dashboards.facility-managers.target"),
            data: Array(30).fill(95),
        },
        {
            name: t("dashboards.facility-managers.actual"),
            data: currentData.value?.equipment?.uptimeTrend || [
                92, 94, 91, 96, 94, 89, 88, 95, 97, 94, 92, 90, 93, 95, 96, 94, 91, 88,
                92, 94, 96, 93, 91, 89, 94, 95, 97, 94, 92, 94,
            ],
        },
    ],
    options: {
        chart: {
            type: "line",
            toolbar: { show: false },
            background: "transparent",
        },
        stroke: { curve: "smooth", width: [2, 3], dashArray: [5, 0] },
        colors: ["#8b92a9", "#3b82f6"],
        xaxis: {
            categories: Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`),
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
            min: 85,
            max: 100,
        },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        legend: { labels: { colors: "#8b92a9" } },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
        },
    },
}));

// Energy System Chart (Treemap) - Fix for undefined data
const energySystemChart = computed(() => ({
    series: [
        {
            data: [
                { x: t("dashboards.facility-managers.hvac"), y: 45 },
                { x: t("dashboards.facility-managers.lighting"), y: 25 },
                { x: t("dashboards.facility-managers.equipment"), y: 15 },
                { x: t("dashboards.facility-managers.security"), y: 8 },
                { x: t("dashboards.facility-managers.other"), y: 7 },
            ],
        },
    ],
    options: {
        chart: {
            type: "treemap",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#22c55e", "#f59e0b", "#8b5cf6", "#ef4444"],
        plotOptions: {
            treemap: {
                enableShades: false,
                shadeIntensity: 0,
                distributed: true,
            },
        },
        dataLabels: {
            enabled: true,
            style: { fontSize: "14px", fontWeight: "bold", colors: ["#ffffff"] },
            formatter: function (text, op) {
                return op && op.value ? [text, op.value + " kWh"] : [text, "0 kWh"];
            },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + " kWh" : "0 kWh";
                },
            },
        },
    },
}));

// Energy Benchmark Chart - Fix formatter functions
const energyBenchmarkChart = computed(() => ({
    series: [
        {
            name: t("dashboards.facility-managers.ourBuilding"),
            data: [2.8, 2.9, 2.7, 2.6, 2.8, 3.0, 3.1, 2.9, 2.7, 2.8, 2.9, 3.0],
        },
        {
            name: t("dashboards.facility-managers.industryAverage"),
            data: [3.2, 3.2, 3.2, 3.2, 3.2, 3.2, 3.2, 3.2, 3.2, 3.2, 3.2, 3.2],
        },
    ],
    options: {
        chart: {
            type: "line",
            toolbar: { show: false },
            background: "transparent",
        },
        stroke: { curve: "smooth", width: [3, 2], dashArray: [0, 5] },
        colors: ["#22c55e", "#8b92a9"],
        xaxis: {
            categories: [
                t("dashboards.renters.jan"),
                t("dashboards.renters.feb"),
                t("dashboards.renters.mar"),
                t("dashboards.renters.apr"),
                t("dashboards.renters.may"),
                t("dashboards.renters.jun"),
                t("dashboards.renters.jul"),
                t("dashboards.renters.aug"),
                t("dashboards.renters.sep"),
                t("dashboards.renters.oct"),
                t("dashboards.renters.nov"),
                t("dashboards.renters.dec"),
            ],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },

        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val.toFixed(1) + " kWh/sq ft" : "0 kWh/sq ft";
                },
            },
        },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        legend: { labels: { colors: "#8b92a9" } },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val.toFixed(1) + " kWh/sq ft" : "0 kWh/sq ft";
                },
            },
        },
    },
}));

// Reactive data
const selectedPeriod = ref("today");

// Chart data
const chartData = {
    today: [
        45, 42, 38, 35, 38, 42, 55, 68, 75, 82, 78, 85, 88, 92, 89, 85, 78, 72, 68,
        65, 58, 52, 48, 46,
    ],
    yesterday: [
        40, 38, 35, 32, 35, 40, 50, 63, 70, 77, 73, 80, 83, 87, 84, 80, 73, 67, 63,
        60, 53, 47, 43, 41,
    ],
    lastWeek: [
        35, 33, 30, 28, 30, 34, 45, 58, 65, 72, 68, 75, 78, 82, 79, 75, 68, 62, 58,
        55, 48, 42, 38, 36,
    ],
};

// Computed chart
const hourlyEnergyChart = computed(() => ({
    series: [
        {
            name: t("dashboards.facility-managers.energyUsage"),
            data: chartData[selectedPeriod.value],
        },
    ],
    options: {
        chart: {
            type: "area",
            toolbar: { show: false },
            background: "transparent",
        },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                colorStops: [
                    { offset: 0, color: "#3b82f6", opacity: 0.8 },
                    { offset: 100, color: "#3b82f6", opacity: 0.1 },
                ],
            },
        },
        stroke: { curve: "smooth", width: 2, colors: ["#3b82f6"] },
        colors: ["#3b82f6"],
        xaxis: {
            categories: Array.from({ length: 24 }, (_, i) => i + ":00"),
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val + " kWh" : "0 kWh";
                },
            },
        },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + " kWh" : "0 kWh";
                },
            },
        },
    },
}));
// Click function
const switchPeriod = (period) => {
    selectedPeriod.value = period;
};

// Space Utilization Chart - Fix data validation
const spaceUtilizationChart = computed(() => ({
    series: [
        {
            name: t("dashboards.facility-managers.currentUsage"),
            data: [85, 72, 45, 68, 92, 35],
        },
        {
            name: t("dashboards.facility-managers.capacity"),
            data: [100, 100, 100, 100, 100, 100],
        },
    ],
    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#e5e7eb"],
        xaxis: {
            categories: [
                t("dashboards.facility-managers.floor1"),
                t("dashboards.facility-managers.floor2"),
                t("dashboards.facility-managers.floor3"),
                t("dashboards.facility-managers.floor4"),
                t("dashboards.facility-managers.floor5"),
                t("dashboards.facility-managers.basement"),
            ],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
            max: 100,
        },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 4 } },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val ? val + "%" : "0%";
            },
            style: { colors: ["#ffffff", "#374151"] },
        },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        legend: { labels: { colors: "#8b92a9" } },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
        },
    },
}));

// Occupancy Heatmap - Fix series data
const occupancyHeatmapChart = computed(() => ({
    series: [
        { name: t("dashboards.facility-managers.monday"), data: [30, 40, 35, 50, 49, 60, 70, 91] },
        { name: t("dashboards.facility-managers.tuesday"), data: [23, 42, 35, 27, 43, 22, 17, 31] },
        { name: t("dashboards.facility-managers.wednesday"), data: [44, 55, 41, 37, 22, 43, 21, 41] },
        { name: t("dashboards.facility-managers.thursday"), data: [30, 47, 35, 50, 49, 60, 70, 81] },
        { name: t("dashboards.facility-managers.friday"), data: [13, 27, 31, 32, 44, 32, 16, 24] },
    ],

    options: {
        chart: {
            type: "heatmap",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6"],
        xaxis: {
            categories: ["9AM", "10AM", "11AM", "12PM", "1PM", "2PM", "3PM", "4PM"],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0.5,
                colorScale: {
                    ranges: [
                        { from: 0, to: 25, name: "Low", color: "#22c55e" },
                        { from: 26, to: 50, name: "Medium", color: "#f59e0b" },
                        { from: 51, to: 100, name: "High", color: "#ef4444" },
                    ],
                },
            },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
        },
    },
}));

// Weekly Occupancy Chart - Fix validation
const weeklyOccupancyChart = computed(() => ({
    series: [
        { name: t("dashboards.facility-managers.occupancy"), data: [142, 158, 135, 167, 145, 89, 65] },
        { name: t("dashboards.facility-managers.capacity"), data: [200, 200, 200, 200, 200, 200, 200] },
    ],

    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#e5e7eb"],
        xaxis: {
            categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? Math.round(val) : "0";
                },
            },
        },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 4 } },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val ? Math.round(val) : "0";
            },
            style: { colors: ["#ffffff", "#374151"] },
        },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        legend: { labels: { colors: "#8b92a9" } },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? Math.round(val) + " people" : "0 people";
                },
            },
        },
    },
}));

// All remaining charts with proper error handling
// HVAC Performance Chart
const hvacPerformanceChart = computed(() => ({
    series: [
        {
            name: "Zone Performance",
            data: [
                [22.5, 85],
                [23.1, 78],
                [21.8, 92],
                [24.2, 65],
                [22.0, 88],
                [23.5, 72],
                [21.5, 95],
            ],
        },
    ],
    options: {
        chart: {
            type: "scatter",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#22c55e"],
        xaxis: {
            title: {
                text: t("dashboards.facility-managers.temperatureC"),
                style: { color: "#94a3b8" }
            },
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val.toFixed(1) + "°C" : "0°C";
                },
            },
        },
        yaxis: {
            title: {
                text: t("dashboards.facility-managers.efficiencyPercentage"),
                style: { color: "#94a3b8" }
            },
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
        },

        markers: { size: 8 },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        tooltip: {
            theme: "dark",
            custom: function ({ dataPointIndex, w }) {
                const data = w.config.series[0].data[dataPointIndex];
                return `<div style="padding: 8px; background: #232937; border-radius: 4px; color: white;">
                    Temperature: ${data && data[0] ? data[0] + "°C" : "N/A"}<br>
                    Efficiency: ${data && data[1] ? data[1] + "%" : "N/A"}
                </div>`;
            },
        },
    },
}));

// Environmental Compliance Chart
const complianceChart = computed(() => ({
    series: [88],
    options: {
        chart: {
            type: "radialBar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#22c55e"],
        plotOptions: {
            radialBar: {
                hollow: { size: "64%" },
                dataLabels: {
                    show: true,
                    value: {
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#ffffff",
                        formatter: function (val) {
                            return val ? val + "%" : "0%";
                        },
                    },
                },
            },
        },
        labels: [t("dashboards.facility-managers.complianceS")],
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
        },
    },
}));

const envMetric = ref("temperature");

// Environmental Trend Chart
const environmentalTrendChart = computed(() => {
    const data = {
        temperature: [
            21.5, 21.8, 22.2, 22.8, 23.1, 23.5, 23.2, 22.9, 22.5, 22.1, 21.8, 21.5,
            21.2, 21.8, 22.5, 23.1, 23.8, 23.5, 23.2, 22.8, 22.3, 21.9, 21.6, 21.4,
        ],
        humidity: [
            45, 47, 49, 52, 55, 58, 56, 54, 52, 49, 47, 45, 43, 46, 50, 53, 57, 55,
            53, 51, 48, 46, 44, 43,
        ],
        co2: [
            400, 405, 410, 425, 440, 465, 480, 475, 460, 445, 430, 420, 410, 415, 435,
            450, 470, 465, 455, 445, 430, 420, 410, 405,
        ],
        airQuality: [
            85, 87, 89, 92, 88, 85, 82, 79, 81, 84, 86, 88, 90, 87, 84, 81, 78, 80,
            83, 86, 88, 90, 92, 89,
        ],
    };

    return {
        series: [{ name: envMetric.value, data: data[envMetric.value] }],
        options: {
            chart: {
                type: "line",
                toolbar: { show: false },
                background: "transparent",
            },
            stroke: { curve: "smooth", width: 3, colors: ["#f59e0b"] },
            colors: ["#f59e0b"],
            markers: { size: 4, colors: ["#f59e0b"] },
            xaxis: {
                categories: Array.from({ length: 24 }, (_, i) => i + ":00"),
                labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
            },
            yaxis: {
                labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
            },
            grid: { borderColor: "#374151", strokeDashArray: 1 },
            tooltip: { theme: "dark" },
        },
    };
})

// Vendor Scorecard Chart
const vendorScorecardChart = computed(() => ({
    series: [
        { name: t("dashboards.facility-managers.hvacServices"), data: [95, 88, 92, 85, 90] },
        { name: t("dashboards.facility-managers.security"), data: [88, 95, 78, 92, 88] },
        { name: t("dashboards.facility-managers.cleaning"), data: [92, 85, 95, 88, 85] },
    ],
    options: {
        chart: {
            type: "radar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#22c55e", "#f59e0b"],
        xaxis: {
            categories: [
                t("dashboards.facility-managers.slaComplianceCategory"),
                t("dashboards.facility-managers.responseTimeCategory"),
                t("dashboards.facility-managers.qualityCategory"),
                t("dashboards.facility-managers.costEfficiencyCategory"),
                t("dashboards.facility-managers.communicationCategory"),
            ],
            labels: {
                style: {
                    colors: "#8b92a9",
                    fontSize: "11px",
                    fontWeight: 500
                },
                offsetY: 5,
            }
        },
        yaxis: {
            show: false,
            min: 0,
            max: 100,
        },
        fill: { opacity: 0.2 },
        stroke: { show: true, width: 2 },
        markers: { size: 4 },
        legend: {
            labels: { colors: "#8b92a9" },
            fontSize: "13px",
            offsetY: 8
        },
        plotOptions: {
            radar: {
                size: 120,
                polygons: {
                    strokeColors: '#374151',
                    strokeWidth: 1,
                    connectorColors: '#374151',
                    fill: {
                        colors: ['#232937', '#1a1f2e']
                    }
                }
            }
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
        },
    },
}));
// Vendor Cost Chart
const vendorCostChart = computed(() => ({
    series: [
        { name: t("dashboards.facility-managers.actual"), data: [8500, 6200, 4800, 3200, 1800] },
        { name: t("dashboards.facility-managers.budget"), data: [9000, 6500, 5000, 3500, 2000] },
    ],

    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#3b82f6", "#e5e7eb"],
        xaxis: {
            categories: [
                t("dashboards.facility-managers.hvac"),
                t("dashboards.facility-managers.security"),
                t("dashboards.facility-managers.cleaning"),
                t("dashboards.facility-managers.electrical"),
                t("dashboards.facility-managers.plumbing"),
            ],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? "€" + val.toLocaleString() : "€0";
                },
            },
        },
        plotOptions: {
            bar: { horizontal: false, columnWidth: "68%", borderRadius: 4 },
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val ? "€" + val.toLocaleString() : "€0";
            },
            style: { colors: ["#ffffff", "#374151"] },
        },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        legend: { labels: { colors: "#8b92a9" } },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? "€" + val.toLocaleString() : "€0";
                },
            },
        },
    },
}));

// Final set of charts with proper error handling
// Response Time Chart
const responseTimeChart = computed(() => ({
    series: [
        { name: t("dashboards.facility-managers.hvac"), data: [1.2, 1.5, 1.8, 1.3, 1.6, 1.4, 1.5] },
        { name: t("dashboards.facility-managers.electrical"), data: [3.2, 4.1, 3.8, 3.5, 4.2, 3.9, 3.8] },
        { name: t("dashboards.facility-managers.plumbing"), data: [4.8, 5.5, 5.2, 4.9, 5.8, 5.1, 5.2] },
    ],

    options: {
        chart: {
            type: "line",
            toolbar: { show: false },
            background: "transparent",
        },
        stroke: { curve: "smooth", width: 3 },
        colors: ["#22c55e", "#f59e0b", "#ef4444"],
        xaxis: {
            categories: [
                t("dashboards.facility-managers.week1"),
                t("dashboards.facility-managers.week2"),
                t("dashboards.facility-managers.week3"),
                t("dashboards.facility-managers.week4"),
                t("dashboards.facility-managers.week5"),
                t("dashboards.facility-managers.week6"),
                t("dashboards.facility-managers.week7")
            ],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },

        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val.toFixed(1) + "h" : "0h";
                },
            },
        },
        markers: { size: 5 },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        legend: { labels: { colors: "#8b92a9" } },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val.toFixed(1) + " hours" : "0 hours";
                },
            },
        },
    },
}));

// Failure Prediction Chart
const failurePredictionChart = computed(() => ({
    series: [
        {
            name: t("dashboards.facility-managers.equipmentRisk"),
            data: [
                [5, 85],
                [8, 72],
                [12, 68],
                [6, 45],
                [15, 92],
                [10, 35],
                [7, 55],
            ],
        },
    ],

    options: {
        chart: {
            type: "scatter",
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#ef4444"],
        xaxis: {
            title: {
                text: t("dashboards.facility-managers.ageYears"),
                style: { color: "#94a3b8" }
            },
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val + " " + t("dashboards.facility-managers.yearsSuffix") : "0 " + t("dashboards.facility-managers.yearsSuffix");
                },
            },
        },

        yaxis: {
            title: { text: t("dashboards.facility-managers.failureRisk"), style: { color: "#94a3b8" } },
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? val + "%" : "0%";
                },
            },
        },
        markers: { size: 8 },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        tooltip: {
            theme: "dark",
            custom: function ({ dataPointIndex, w }) {
                const data = w.config.series[0].data[dataPointIndex];
                const equipmentNames = [
                    t("dashboards.facility-managers.boilerA"),
                    t("dashboards.facility-managers.hvacB2"),
                    t("dashboards.facility-managers.elevator1"),
                    t("dashboards.facility-managers.pumpC3"),
                    t("dashboards.facility-managers.generator"),
                    t("dashboards.facility-managers.acUnit"),
                    t("dashboards.facility-managers.compressor"),
                ];
                return `<div style="padding: 8px; background: #232937; border-radius: 4px; color: white;">
              <strong>${equipmentNames[dataPointIndex] || t("dashboards.facility-managers.equipment")}</strong><br>
              ${t("dashboards.facility-managers.ageYears")}: ${data && data[0] ? data[0] + " " + t("dashboards.facility-managers.years") : "N/A"}<br>
              ${t("dashboards.facility-managers.risk")}: ${data && data[1] ? data[1] + "%" : "N/A"}
            </div>`;
            }
        },
    },
}));

// Cost Forecast Chart
const costForecastChart = computed(() => ({
    series: [
        {
            name: t("dashboards.facility-managers.predicted"),
            data: [
                28000, 32000, 29000, 35000, 31000, 38000,
                42000, 39000, 36000, 33000, 30000, 34000
            ],
        },
        {
            name: t("dashboards.facility-managers.historical"),
            data: [26000, 28000, 31000, 29000, 33000, 35000],
        },
    ],

    options: {
        chart: {
            type: "line",
            toolbar: { show: false },
            background: "transparent",
        },
        stroke: { curve: "smooth", width: [3, 2], dashArray: [0, 5] },
        colors: ["#3b82f6", "#8b92a9"],
        xaxis: {
            categories: [
                t("dashboards.renters.jan"),
                t("dashboards.renters.feb"),
                t("dashboards.renters.mar"),
                t("dashboards.renters.apr"),
                t("dashboards.renters.may"),
                t("dashboards.renters.jun"),
                t("dashboards.renters.jul"),
                t("dashboards.renters.aug"),
                t("dashboards.renters.sep"),
                t("dashboards.renters.oct"),
                t("dashboards.renters.nov"),
                t("dashboards.renters.dec"),
            ],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? "€" + val / 1000 + "k" : "€0";
                },
            },
        },
        markers: { size: 5 },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        legend: { labels: { colors: "#8b92a9" } },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? "€" + val.toLocaleString() : "€0";
                },
            },
        },
    },
}));

// Asset Lifecycle Chart
const assetLifecycleChart = computed(() => ({
    series: [
        { name: t("dashboards.facility-managers.new03"), data: [12, 8, 5, 15, 3, 20] },
        { name: t("dashboards.facility-managers.good37"), data: [25, 18, 12, 22, 8, 15] },
        { name: t("dashboards.facility-managers.aging712"), data: [18, 15, 20, 10, 12, 8] },
        { name: t("dashboards.facility-managers.replace12"), data: [8, 12, 15, 5, 18, 3] }
    ],

    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
            stacked: true,
        },
        colors: ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444"],
        xaxis: {
            categories: [
                t("dashboards.facility-managers.hvac"),
                t("dashboards.facility-managers.electrical"),
                t("dashboards.facility-managers.plumbing"),
                t("dashboards.facility-managers.security"),
                t("dashboards.facility-managers.elevators"),
                t("dashboards.facility-managers.lighting"),
            ],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                style: { colors: "#94a3b8", fontSize: "12px" },
                formatter: function (val) {
                    return val ? Math.round(val) : "0";
                },
            },
        },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 4 } },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val && val > 0 ? Math.round(val) : "";
            },
            style: { colors: ["#ffffff"] },
        },
        grid: { borderColor: "#374151", strokeDashArray: 1 },
        legend: { labels: { colors: "#8b92a9" } },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? val + " assets" : "0 assets";
                },
            },
        },
    },
}));

// Maintenance Type Chart - Fix with proper validation
const maintenanceTypeChart = computed(() => ({
    series: [65, 35],
    options: {
        chart: {
            type: "donut",
            background: "transparent",
            toolbar: { show: false },
        },
        colors: ["#22c55e", "#ef4444"],
        labels: [
            t("dashboards.facility-managers.preventive"),
            t("dashboards.facility-managers.reactive")
        ],

        legend: {
            position: "bottom",
            labels: { colors: "#8b92a9" },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: "65%",
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: t("dashboards.facility-managers.uptimeTarget75"),
                            color: "#8b92a9",
                            formatter: function () {
                                return "65%";
                            },
                        },
                    },
                },
            },
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val ? Math.round(val) + "%" : "0%";
            },
            style: { colors: ["#ffffff"] },
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val ? Math.round(val) + "%" : "0%";
                },
            },
        },
    },
}));
</script>

<style scoped>
@import "@/components/OneVrAnalytics/AnalyticsDashboardStyles/FacilityManagement.css";
</style>